import { useState, useEffect, useCallback, useMemo } from "react";
import {
  Plus,
  Trash2,
  Edit2,
  BarChart3,
  Shield,
  Calendar,
  Shuffle,
  RefreshCw,
  Eye,
  X,
} from "lucide-react";

export const COMMUNICATION_POLLS_STORAGE_KEY = "communicationAdvancedPolls";

function loadPolls() {
  try {
    const raw = localStorage.getItem(COMMUNICATION_POLLS_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function savePollsToStorage(polls) {
  try {
    localStorage.setItem(COMMUNICATION_POLLS_STORAGE_KEY, JSON.stringify(polls));
  } catch {
    /* ignore */
  }
}

const defaultForm = () => ({
  title: "",
  description: "",
  questionType: "single",
  options: ["Option A", "Option B"],
  anonymous: false,
  showResults: "after_vote",
  randomizeOrder: false,
  allowChangeVote: false,
  startDate: "",
  endDate: "",
  status: "draft",
  assignmentType: "all",
});

const LIKERT_DEFAULT = [
  "Strongly disagree",
  "Disagree",
  "Neutral",
  "Agree",
  "Strongly agree",
];

function PollForm({
  initial,
  onSave,
  onCancel,
  embedded,
}) {
  const mergeInitial = (src) => {
    if (!src) return defaultForm();
    const base = defaultForm();
    return {
      ...base,
      ...src,
      options:
        Array.isArray(src.options) && src.options.length > 0
          ? src.options
          : base.options,
    };
  };

  const [form, setForm] = useState(() => mergeInitial(initial));

  useEffect(() => {
    setForm(mergeInitial(initial));
  }, [initial]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleTypeChange = (questionType) => {
    let options = form.options;
    if (questionType === "likert") options = [...LIKERT_DEFAULT];
    else if (questionType === "stars") options = ["1", "2", "3", "4", "5"];
    else if (questionType === "single" || questionType === "multi") {
      options = options.length < 2 ? ["Option A", "Option B"] : options;
    }
    setForm((f) => ({ ...f, questionType, options }));
  };

  const addOption = () =>
    setForm((f) => ({
      ...f,
      options: [...f.options, `Option ${f.options.length + 1}`],
    }));

  const setOptionAt = (i, v) =>
    setForm((f) => {
      const next = [...f.options];
      next[i] = v;
      return { ...f, options: next };
    });

  const removeOption = (i) =>
    setForm((f) => ({
      ...f,
      options: f.options.filter((_, j) => j !== i),
    }));

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.title?.trim()) return;
    const opts = form.options.map((o) => String(o).trim()).filter(Boolean);
    if (form.questionType !== "stars" && form.questionType !== "likert" && opts.length < 2) {
      return;
    }
    onSave({
      ...form,
      id: form.id ?? initial?.id,
      options: form.questionType === "stars" ? ["1", "2", "3", "4", "5"] : opts,
    });
  };

  const boxClass = embedded
    ? "rounded-lg border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] p-4 dark:border-[hsl(var(--ds-border))] dark:bg-[hsl(var(--ds-surface))]"
    : "";

  return (
    <form onSubmit={handleSubmit} className={`flex flex-col gap-4 ${boxClass}`}>
      <div>
        <label className="block text-xs font-medium text-[hsl(var(--ds-muted-fg))] mb-1">Poll title *</label>
        <input
          type="text"
          value={form.title}
          onChange={(e) => setField("title", e.target.value)}
          className="w-full border border-[hsl(var(--ds-border))] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--ds-ring))]/35"
          placeholder="e.g. Q4 engagement pulse"
        />
      </div>
      <div>
        <label className="block text-xs font-medium text-[hsl(var(--ds-muted-fg))] mb-1">Description / context</label>
        <textarea
          value={form.description}
          onChange={(e) => setField("description", e.target.value)}
          rows={3}
          className="w-full border border-[hsl(var(--ds-border))] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--ds-ring))]/35"
          placeholder="Optional instructions shown above the question"
        />
      </div>

      <div>
        <label className="block text-xs font-medium text-[hsl(var(--ds-muted-fg))] mb-1">Question type</label>
        <select
          value={form.questionType}
          onChange={(e) => handleTypeChange(e.target.value)}
          className="w-full border border-[hsl(var(--ds-border))] px-3 py-2 text-sm bg-[hsl(var(--ds-surface))] focus:outline-none focus:ring-2 focus:ring-[hsl(var(--ds-ring))]/35"
        >
          <option value="single">Single choice</option>
          <option value="multi">Multiple choice (advanced)</option>
          <option value="likert">Likert scale (5)</option>
          <option value="stars">Star rating (1–5)</option>
        </select>
      </div>

      {(form.questionType === "single" || form.questionType === "multi") && (
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-xs font-medium text-[hsl(var(--ds-muted-fg))]">Answer options</label>
            <button
              type="button"
              onClick={addOption}
              className="text-xs text-[hsl(var(--ds-secondary))] hover:underline flex items-center gap-1"
            >
              <Plus className="w-3 h-3" /> Add option
            </button>
          </div>
          <div className="space-y-2">
            {form.options.map((opt, i) => (
              <div key={i} className="flex gap-2 items-center">
                <input
                  type="text"
                  value={opt}
                  onChange={(e) => setOptionAt(i, e.target.value)}
                  className="flex-1 border border-[hsl(var(--ds-border))] px-2 py-1.5 text-sm"
                />
                {form.options.length > 2 && (
                  <button
                    type="button"
                    onClick={() => removeOption(i)}
                    className="text-red-600 p-1"
                    aria-label="Remove option"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {form.questionType === "likert" && (
        <p className="text-xs text-[hsl(var(--ds-muted-fg))]">
          Standard 5-point Likert labels are used. You can edit each label below.
        </p>
      )}
      {form.questionType === "likert" &&
        form.options.map((opt, i) => (
          <input
            key={i}
            type="text"
            value={opt}
            onChange={(e) => setOptionAt(i, e.target.value)}
            className="w-full border border-[hsl(var(--ds-border))] px-2 py-1.5 text-sm"
          />
        ))}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 border-t border-[hsl(var(--ds-border))] pt-3">
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input
            type="checkbox"
            checked={form.anonymous}
            onChange={(e) => setField("anonymous", e.target.checked)}
          />
          <Shield className="w-4 h-4 text-[hsl(var(--ds-muted-fg))]" />
          Anonymous responses
        </label>
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input
            type="checkbox"
            checked={form.randomizeOrder}
            onChange={(e) => setField("randomizeOrder", e.target.checked)}
          />
          <Shuffle className="w-4 h-4 text-[hsl(var(--ds-muted-fg))]" />
          Randomize option order
        </label>
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input
            type="checkbox"
            checked={form.allowChangeVote}
            onChange={(e) => setField("allowChangeVote", e.target.checked)}
          />
          <RefreshCw className="w-4 h-4 text-[hsl(var(--ds-muted-fg))]" />
          Allow change vote before close
        </label>
      </div>

      <div>
        <label className="block text-xs font-medium text-[hsl(var(--ds-muted-fg))] mb-1">When to show results</label>
        <select
          value={form.showResults}
          onChange={(e) => setField("showResults", e.target.value)}
          className="w-full border border-[hsl(var(--ds-border))] px-3 py-2 text-sm bg-[hsl(var(--ds-surface))]"
        >
          <option value="after_vote">After employee votes</option>
          <option value="after_close">After poll closes</option>
          <option value="admin_only">Admins only (summary in Manage)</option>
        </select>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-[hsl(var(--ds-muted-fg))] mb-1">Start (optional)</label>
          <input
            type="datetime-local"
            value={form.startDate}
            onChange={(e) => setField("startDate", e.target.value)}
            className="w-full border border-[hsl(var(--ds-border))] px-2 py-1.5 text-sm"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-[hsl(var(--ds-muted-fg))] mb-1">End (optional)</label>
          <input
            type="datetime-local"
            value={form.endDate}
            onChange={(e) => setField("endDate", e.target.value)}
            className="w-full border border-[hsl(var(--ds-border))] px-2 py-1.5 text-sm"
          />
        </div>
      </div>

      <div>
        <label className="block text-xs font-medium text-[hsl(var(--ds-muted-fg))] mb-1">Audience</label>
        <div className="flex gap-4 text-sm">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name="pollAudience"
              checked={form.assignmentType === "all"}
              onChange={() => setField("assignmentType", "all")}
            />
            All employees
          </label>
        </div>
      </div>

      <div>
        <label className="block text-xs font-medium text-[hsl(var(--ds-muted-fg))] mb-1">Initial status</label>
        <select
          value={form.status}
          onChange={(e) => setField("status", e.target.value)}
          className="w-full border border-[hsl(var(--ds-border))] px-3 py-2 text-sm bg-[hsl(var(--ds-surface))]"
        >
          <option value="draft">Draft (not visible)</option>
          <option value="active">Active (open)</option>
        </select>
      </div>

      <div className="flex justify-end gap-2 pt-2 border-t border-[hsl(var(--ds-border))]">
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-sm border border-[hsl(var(--ds-border))] hover:bg-[hsl(var(--ds-muted))]"
          >
            Cancel
          </button>
        )}
        <button
          type="submit"
          className="px-4 py-2 text-sm inline-flex items-center justify-center rounded-md bg-[hsl(var(--ds-primary))] px-3.5 py-2 text-sm font-semibold text-[hsl(var(--ds-primary-fg))] hover:bg-[hsl(var(--ds-primary-hover))] disabled:opacity-45 hover:opacity-95"
        >
          {initial?.id ? "Update poll" : "Save poll"}
        </button>
      </div>
    </form>
  );
}

/**
 * Advanced poll builder (mode=advanced) and manage list (mode=manage).
 */
export default function CommunicationPollsPanel({
  mode,
  onNavigateAdvanced,
  canCreatePolls = true,
  canEditPolls = true,
  canDeletePolls = true,
}) {
  const [polls, setPolls] = useState(loadPolls);
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null);
  const [showFormModal, setShowFormModal] = useState(false);

  useEffect(() => {
    savePollsToStorage(polls);
  }, [polls]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return polls;
    return polls.filter(
      (p) =>
        p.title?.toLowerCase().includes(q) ||
        p.description?.toLowerCase().includes(q)
    );
  }, [polls, search]);

  const handleSavePoll = useCallback(
    (data) => {
      if (!canCreatePolls && !(data?.id && canEditPolls)) return;
      const id = data.id || Date.now();
      const row = {
        ...data,
        id,
        updatedAt: new Date().toISOString(),
        createdAt: data.createdAt || new Date().toISOString(),
        responseCount: data.responseCount ?? 0,
      };
      setPolls((prev) => {
        const idx = prev.findIndex((p) => p.id === id);
        if (idx >= 0) {
          const next = [...prev];
          next[idx] = { ...prev[idx], ...row };
          return next;
        }
        return [...prev, row];
      });
      setEditing(null);
      setShowFormModal(false);
    },
    [canCreatePolls, canEditPolls]
  );

  const handleDelete = (id) => {
    if (!canDeletePolls) return;
    if (!window.confirm("Delete this poll permanently?")) return;
    setPolls((prev) => prev.filter((p) => p.id !== id));
  };

  const handleStatus = (id, status) => {
    if (!canEditPolls) return;
    setPolls((prev) =>
      prev.map((p) => (p.id === id ? { ...p, status, updatedAt: new Date().toISOString() } : p))
    );
  };

  if (mode === "advanced") {
    return (
      <div className="flex-1 flex flex-col overflow-hidden min-h-0">
        <div className="flex justify-between items-center p-4 border-b border-[hsl(var(--ds-border))] flex-shrink-0">
          <div>
            <h3 className="text-lg font-semibold flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-[hsl(var(--ds-secondary))]" />
              Advanced polls
            </h3>
            <p className="text-xs text-[hsl(var(--ds-muted-fg))] mt-1">
              Configure question types, anonymity, visibility of results, and lifecycle.
            </p>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-4">
          <PollForm onSave={handleSavePoll} embedded />
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col overflow-hidden min-h-0">
      <div className="flex justify-between items-center p-4 border-b border-[hsl(var(--ds-border))] flex-shrink-0 gap-4 flex-wrap">
        <div>
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-[hsl(var(--ds-secondary))]" />
            Manage polls
          </h3>
          <p className="text-xs text-[hsl(var(--ds-muted-fg))] mt-1">Edit, close, or remove polls. Response counts are stored locally.</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => onNavigateAdvanced?.()}
            disabled={!canCreatePolls}
            className="flex items-center gap-2 inline-flex items-center justify-center rounded-md bg-[hsl(var(--ds-primary))] px-3.5 py-2 text-sm font-semibold text-[hsl(var(--ds-primary-fg))] hover:bg-[hsl(var(--ds-primary-hover))] disabled:opacity-45 px-3 py-2 text-sm hover:opacity-95"
          >
            <Plus className="w-4 h-4" />
            New advanced poll
          </button>
        </div>
      </div>

      <div className="p-4 border-b border-[hsl(var(--ds-border))] flex-shrink-0">
        <input
          type="search"
          placeholder="Search polls..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full max-w-md border border-[hsl(var(--ds-border))] px-3 py-2 text-sm"
        />
      </div>

      <div className="flex-1 overflow-y-auto p-4">
        {filtered.length === 0 ? (
          <p className="text-[hsl(var(--ds-muted-fg))] text-center py-8">
            No polls yet. Use <strong>Advanced polls</strong> to create one, or click &quot;New advanced poll&quot;.
          </p>
        ) : (
          <div className="space-y-2">
            {filtered.map((p) => (
              <div
                key={p.id}
                className="flex flex-wrap items-start justify-between gap-3 border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))] p-3 dark:border-[hsl(var(--ds-border))] dark:bg-[hsl(var(--ds-surface))]"
              >
                <div className="min-w-0 flex-1">
                  <h4 className="truncate text-sm font-semibold text-[hsl(var(--ds-fg))] dark:text-[hsl(var(--ds-fg))]">{p.title}</h4>
                  <p className="mt-0.5 line-clamp-2 text-xs text-[hsl(var(--ds-muted-fg))] dark:text-[hsl(var(--ds-muted-fg))]">{p.description || "—"}</p>
                  <div className="flex flex-wrap gap-2 mt-2">
                    <span className="text-[10px] px-2 py-0.5 bg-[hsl(var(--ds-muted))] rounded">
                      {p.questionType === "single"
                        ? "Single"
                        : p.questionType === "multi"
                        ? "Multi"
                        : p.questionType === "likert"
                        ? "Likert"
                        : "Stars"}
                    </span>
                    <span
                      className={`rounded px-2 py-0.5 text-[10px] ${
                        p.status === "active"
                          ? "border border-emerald-200/80 bg-emerald-50 text-emerald-900 dark:border-emerald-800/50 dark:bg-emerald-950/40 dark:text-emerald-300"
                          : p.status === "draft"
                          ? "border border-amber-200/80 bg-amber-50 text-amber-900 dark:border-amber-800/50 dark:bg-amber-950/40 dark:text-amber-300"
                          : "bg-[hsl(var(--ds-muted))] text-[hsl(var(--ds-fg))] dark:bg-[hsl(var(--ds-muted))] dark:text-[hsl(var(--ds-muted-fg))]"
                      }`}
                    >
                      {p.status || "draft"}
                    </span>
                    {p.anonymous && (
                      <span className="text-[10px] px-2 py-0.5 bg-[hsl(var(--ds-accent))] text-[hsl(var(--ds-secondary))]">Anonymous</span>
                    )}
                    <span className="text-[10px] text-[hsl(var(--ds-muted-fg))] flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {p.endDate ? new Date(p.endDate).toLocaleString() : "No end date"}
                    </span>
                    <span className="text-[10px] text-[hsl(var(--ds-muted-fg))]">
                      Responses: {p.responseCount ?? 0}
                    </span>
                  </div>
                </div>
                <div className="flex flex-wrap gap-1 items-center">
                  <button
                    type="button"
                    onClick={() => {
                      if (!canEditPolls) return;
                      setEditing(p);
                      setShowFormModal(true);
                    }}
                    disabled={!canEditPolls}
                    className="p-2 text-[hsl(var(--ds-secondary))] hover:bg-[hsl(var(--ds-accent))] border border-transparent hover:border-[hsl(var(--ds-border))]"
                    title="Edit"
                  >
                    <Edit2 className="w-4 h-4" />
                  </button>
                  {p.status === "active" && (
                    <button
                      type="button"
                      onClick={() => handleStatus(p.id, "closed")}
                      disabled={!canEditPolls}
                      className="rounded border border-[hsl(var(--ds-warning))] px-2 py-1 text-xs text-[hsl(var(--ds-warning))] transition hover:bg-orange-50 dark:hover:bg-[hsl(var(--ds-muted))]"
                    >
                      Close
                    </button>
                  )}
                  {p.status === "draft" && (
                    <button
                      type="button"
                      onClick={() => handleStatus(p.id, "active")}
                      disabled={!canEditPolls}
                      className="rounded border border-emerald-300 px-2 py-1 text-xs text-emerald-800 transition hover:bg-emerald-50 dark:border-emerald-700 dark:text-emerald-300 dark:hover:bg-emerald-950/40"
                    >
                      Publish
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => handleDelete(p.id)}
                    disabled={!canDeletePolls}
                    className="p-2 text-red-600 hover:bg-red-50"
                    title="Delete"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showFormModal && editing && (
        <div className="fixed inset-0 z-[140] flex items-center justify-center bg-black/50 p-4">
          <div className="relative max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-lg border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] shadow-xl dark:border-[hsl(var(--ds-border))] dark:bg-[hsl(var(--ds-surface))]">
            <div className="sticky top-0 flex items-center justify-between border-b border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] px-4 py-2 dark:border-[hsl(var(--ds-border))] dark:bg-[hsl(var(--ds-surface))]">
              <span className="text-sm font-semibold text-[hsl(var(--ds-fg))] dark:text-[hsl(var(--ds-fg))]">Edit poll</span>
              <button
                type="button"
                onClick={() => {
                  setShowFormModal(false);
                  setEditing(null);
                }}
                className="p-1 rounded hover:bg-[hsl(var(--ds-muted))]"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4">
              <PollForm
                initial={editing}
                embedded
                onSave={(data) => handleSavePoll({ ...data, id: editing.id, createdAt: editing.createdAt })}
                onCancel={() => {
                  setShowFormModal(false);
                  setEditing(null);
                }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
